import type {
  CancellationPolicy,
  HotelBookableOption,
  HotelBookableOptionAlternativeRoom,
  HotelBookableOptionWithAlternativeRoom,
  HotelBookableOptionWithPax,
  HotelBookableRoom,
  HotelIngredientAdapter,
  HotelIngredientResponse,
  Pax,
  SimplifiedPax,
  RoomPax,
  UnavailableHotelIngredientResponse,
  TaxesAndFee,
  HotelAlternativeOptionWithBasicRooms,
} from '@libs/trip-planner-interfaces';
import { IngredientType, BoardBasisCode } from '@libs/trip-planner-interfaces';
import { assertValue, isDefined } from '@libs/utils';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import type { IngredientResponseQL } from './itineraryResponseQL';
import { toMoney } from './moneyResponseQL';
import { toActivityIngredientResponse } from './activityIngredientResponseQL';

export type HotelIngredientResponseQL = Extract<
  IngredientResponseQL,
  { type: 'HotelIngredientResponseQL' }
>;
export function toHotelIngredientResponse(
  hotelIngredientResponseQL: HotelIngredientResponseQL,
  hotelIngredientAdapter: HotelIngredientAdapter
): HotelIngredientResponse {
  const suggested = isDefined(hotelIngredientResponseQL.suggested)
    ? getSuggestedFromResponse(hotelIngredientResponseQL.suggested)
    : null;

  const unavailableHotelIngredientResponse =
    toUnavailableHotelIngredientResponse(
      hotelIngredientResponseQL,
      hotelIngredientAdapter
    );

  if (suggested) {
    return {
      ...unavailableHotelIngredientResponse,
      isAvailable: true,
      suggested: suggested.withPax,
      suggestions: hotelIngredientResponseQL.suggestions
        ? hotelIngredientResponseQL.suggestions.map(toHotelSuggestion)
        : [],
      options: [suggested.basic],
      addToBasketToken: suggested.basic.addToBasketToken,
      alternativeSuggestion: null,
    };
  } else {
    return unavailableHotelIngredientResponse;
  }
}

type SuggestedQL = NonNullable<HotelIngredientResponseQL['suggested']>;
function getSuggestedFromResponse(responseSuggested: SuggestedQL): {
  basic: HotelBookableOption;
  withPax: HotelBookableOptionWithPax | HotelBookableOptionWithAlternativeRoom;
} {
  assertSmartSearchList(responseSuggested.rooms);
  assertSmartSearchValue(responseSuggested.totalPrice);

  const rooms = responseSuggested.rooms.map(toHotelBookableRoom);
  const hotelBookableOption: HotelBookableOption = {
    addToBasketToken: responseSuggested.addToBasketToken,
    code: responseSuggested.code,
    totalPrice: toMoney(responseSuggested.totalPrice),
    isRefundable: responseSuggested.isRefundable,
  };

  return {
    basic: hotelBookableOption,
    withPax: {
      ...hotelBookableOption,
      pax: getTotalPax(rooms),
      rooms: rooms.map((roomObject) => ({
        description: roomObject.description,
        cancellationPolicies: roomObject.cancellationPolicies,
        taxesAndFees: roomObject.taxesAndFees,
        price: roomObject.price,
        boardBasis: roomObject.boardBasis,
      })),
    },
  };
}

type SuggestedRoomsQL = NonNullable<SuggestedQL['rooms']>;
type SuggestedRoomQL = NonNullable<SuggestedRoomsQL[number]>;
function toHotelBookableRoom(
  suggestedRoomQL: SuggestedRoomQL
): HotelBookableRoom {
  assertSmartSearchValue(suggestedRoomQL.boardBasis);
  assertSmartSearchList(suggestedRoomQL.cancellationPolicies);
  assertSmartSearchList(suggestedRoomQL.pax);
  assertSmartSearchValue(suggestedRoomQL.price);
  assertSmartSearchList(suggestedRoomQL.taxesAndFees);

  return {
    boardBasis: toBoardBasisCode(suggestedRoomQL.boardBasis),
    cancellationPolicies:
      suggestedRoomQL.cancellationPolicies.map(toCancellationPolicy),
    description: suggestedRoomQL.description,
    pax: suggestedRoomQL.pax.map(toSimplifiedPax),
    price: toMoney(suggestedRoomQL.price),
    taxesAndFees: suggestedRoomQL.taxesAndFees.map(toTaxAndFee),
  };
}

function toBoardBasisCode(boardBasis: string): BoardBasisCode {
  const boardBasisCode = Object.values(BoardBasisCode).find(
    (value) => value === boardBasis
  );

  assertValue(boardBasisCode, 'Unknown board basis');

  return boardBasisCode;
}

type CancellationPoliciesQL = NonNullable<
  SuggestedRoomQL['cancellationPolicies']
>;
type CancellationPolicyQL = NonNullable<CancellationPoliciesQL[number]>;
function toCancellationPolicy(
  cancellationPolicyQL: CancellationPolicyQL
): CancellationPolicy {
  assertSmartSearchValue(cancellationPolicyQL.amount);

  return {
    amount: toMoney(cancellationPolicyQL.amount),
    fromDate: cancellationPolicyQL.fromDate,
    fromDateIso: cancellationPolicyQL.fromDateIso,
  };
}

type PaxQL = NonNullable<SuggestedRoomQL['pax']>[number];
function toSimplifiedPax(paxQL: PaxQL): SimplifiedPax {
  return {
    age: paxQL?.age ?? null,
  };
}

type TaxesAndFeesQL = NonNullable<SuggestedRoomQL['taxesAndFees']>;
type TaxAndFeeQL = NonNullable<TaxesAndFeesQL[number]>;
function toTaxAndFee(taxAndFeeQL: TaxAndFeeQL): TaxesAndFee {
  assertSmartSearchValue(taxAndFeeQL.amount);

  return {
    amount: toMoney(taxAndFeeQL.amount),
    isIncludedInPrice: taxAndFeeQL.isIncludedInPrice,
    type: taxAndFeeQL.type,
  };
}

function getTotalPax(rooms: HotelBookableRoom[]): Pax {
  return rooms.reduce(
    (
      totalPax: Pax,
      room: HotelBookableOptionAlternativeRoom | HotelBookableRoom
    ) => {
      const roomPax: RoomPax = room.pax.reduce(
        (roomPax: RoomPax, currentRoomPax: SimplifiedPax) => {
          if (currentRoomPax.age === null) {
            roomPax.numberOfAdults++;
          } else {
            roomPax.childAges
              ? roomPax.childAges.push(currentRoomPax.age)
              : (roomPax.childAges = [currentRoomPax.age]);
          }
          return roomPax;
        },
        { numberOfAdults: 0 }
      );
      totalPax.push(roomPax);
      return totalPax;
    },
    []
  );
}

type SuggestionsQL = NonNullable<HotelIngredientResponseQL['suggestions']>;
type SuggestionQL = NonNullable<SuggestionsQL[number]>;
function toHotelSuggestion(
  suggestionQL: SuggestionQL
): HotelAlternativeOptionWithBasicRooms {
  assertSmartSearchValue(suggestionQL.prices?.deltaPrice);
  assertSmartSearchValue(suggestionQL.prices?.totalPrice);
  assertSmartSearchList(suggestionQL.rooms);

  const deltaPrice = toMoney(suggestionQL.prices.deltaPrice);
  const totalPrice = toMoney(suggestionQL.prices.totalPrice);
  return {
    addToBasketToken: suggestionQL.addToBasketToken,
    code: suggestionQL.code,
    isRefundable: suggestionQL.isRefundable,
    prices: {
      deltaPrice,
      totalPrice,
    },
    rooms: suggestionQL.rooms.map(toHotelBookableRoom),
    totalPrice,
  };
}

function toUnavailableHotelIngredientResponse(
  result: HotelIngredientResponseQL,
  ingredient: HotelIngredientAdapter
): UnavailableHotelIngredientResponse {
  assertSmartSearchValue(result.date);
  assertSmartSearchValue(result.lengthOfStay);

  return {
    suggested: null,
    suggestions: null,
    options: [],
    addToBasketToken: null,
    date: new Date(result.date),
    numberOfNights: result.lengthOfStay.numberOfNights,
    isNumberOfNightsSuggested: result.lengthOfStay.nightsHaveBeenSuggested,
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    subIngredientResponse:
      result.subIngredientResults?.map((subResult, subIndex) => {
        assertSmartSearchValue(subResult);

        const subIngredient = ingredient.subIngredients[subIndex];

        assertValue(
          subIngredient,
          'search-service-smart-search > mappers.ts > toHotelIngredientResponse > subIngredient'
        );

        if (subResult.type === 'TicketIngredientResponseQL') {
          return toActivityIngredientResponse(subResult, subIngredient);
        } else {
          throw new Error('Unhandled subIngredient type');
        }
      }) ?? [],
    isAvailable: false,
    alternativeSuggestion: result.alternativeSuggestion
      ? toAlternativeSuggestion(result.alternativeSuggestion)
      : null,
  };
}

type AlternativeSuggestionQL = NonNullable<
  HotelIngredientResponseQL['alternativeSuggestion']
>;
function toAlternativeSuggestion(
  alternativeSuggestionQL: AlternativeSuggestionQL
): HotelBookableOptionWithAlternativeRoom {
  assertSmartSearchList(alternativeSuggestionQL.rooms);
  assertSmartSearchValue(alternativeSuggestionQL.totalPrice);

  const rooms = alternativeSuggestionQL.rooms.map(
    toHotelBookableOptionAlternativeRoom
  );

  return {
    addToBasketToken: alternativeSuggestionQL.addToBasketToken,
    code: alternativeSuggestionQL.code,
    isRefundable: alternativeSuggestionQL.isRefundable,
    rooms,
    totalPrice: toMoney(alternativeSuggestionQL.totalPrice),
    pax: getTotalPax(rooms),
  };
}

type AlternativeSuggestionRoomsQL = NonNullable<
  AlternativeSuggestionQL['rooms']
>;
type AlternativeSuggestionRoomQL = NonNullable<
  AlternativeSuggestionRoomsQL[number]
>;
function toHotelBookableOptionAlternativeRoom(
  alternativeSuggestionRoomQL: AlternativeSuggestionRoomQL
): HotelBookableOptionAlternativeRoom {
  return {
    ...toHotelBookableRoom(alternativeSuggestionRoomQL),
    roomCode: alternativeSuggestionRoomQL.roomCode,
  };
}
